import React, { useEffect, useState } from "react";
import { MessageCircle, MapPin, Mail, Clock, Phone } from "lucide-react";
import { useLang, pick } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { openWhatsApp, websitePhoneLabel, WEBSITE_PHONE_DEFAULT } from "@/lib/whatsapp";
import SeoHead from "@/components/SeoHead";
import { ROUTE_KEYS } from "@/i18n/routeMap";

export default function Contact() {
  const { lang, t } = useLang();
  const [s, setS] = useState({});

  useEffect(() => {
    document.title = `${t.contact.title} · Spain Luxury Rentals`;
    (async () => { try { const { data } = await api.get("/settings"); setS(data || {}); } catch { /* ignore */ } })();
  }, [t.contact.title]);

  // Prompt 8 — both phones now point at the website number. The social
  // number is intentionally NOT shown on the public site.
  const phone = websitePhoneLabel(s) || WEBSITE_PHONE_DEFAULT;
  const wa = phone;

  return (
    <div className="bg-[#0C0C0C] pt-32 md:pt-40 pb-24 min-h-screen" data-testid="contact-page">
      <SeoHead
        pageKey={ROUTE_KEYS.CONTACT}
        lang={lang}
        title={`${t.contact.title} · Spain Luxury Rentals`}
        description={t.contact.title}
      />
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-overline text-[#C9A55A] mb-4">{t.contact.kicker}</div>
        <h1 className="font-display text-[#F5F1EB] text-4xl md:text-6xl max-w-3xl">{t.contact.title}</h1>
        <p className="mt-5 text-[#F5F1EB]/70 font-light max-w-2xl">{t.contact.subtitle}</p>

        <button onClick={() => openWhatsApp(t.whatsapp.generic, s, lang)} className="btn-red mt-10" data-testid="contact-page-whatsapp">
          <MessageCircle size={16} /> {t.contact.cta}
        </button>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <InfoBlock icon={Phone} label={t.contact.phone} value={phone} href={`tel:${phone.replace(/\s/g, "")}`} testid="info-phone" />
          <InfoBlock icon={MessageCircle} label={t.contact.reservations} value={wa} accent testid="info-wa" />
          <InfoBlock icon={Mail} label={t.contact.email} value={s.email || "spainluxuryrentacar@gmail.com"} href={`mailto:${s.email || "spainluxuryrentacar@gmail.com"}`} testid="info-email" />
          <InfoBlock icon={Clock} label={t.contact.hours} value={pick(s.openingHours, lang) || "Open 24H"} testid="info-hours" />
          <InfoBlock icon={MapPin} label={t.contact.location} value={pick(s.address, lang) || "Benidorm, Alicante, Costa Blanca"} testid="info-address" />
        </div>
      </div>
    </div>
  );
}

function InfoBlock({ icon: Icon, label, value, href, accent, testid }) {
  const content = (
    <>
      <Icon className={accent ? "text-[#B8342A] mb-3" : "text-[#C9A55A] mb-3"} size={18} />
      <div className="text-overline text-[#C9A55A] mb-2">{label}</div>
      <div className={`${accent ? "text-[#B8342A]" : "text-[#F5F1EB]"} font-semibold tracking-wide`}>{value}</div>
    </>
  );
  return href ? (
    <a href={href} className="border border-[#2A2A2A] hover:border-[#C9A55A] transition-colors p-6 block" data-testid={testid}>
      {content}
    </a>
  ) : (
    <div className="border border-[#2A2A2A] p-6" data-testid={testid}>{content}</div>
  );
}
